/**
 * The console's greeting: the time of day, and the reader's name.
 *
 * ## Why this is three branches and not the product's nine-rung ladder
 *
 * It used to import `pickGreeting` from the phone (`backend/src/frontend/lib/
 * greeting.ts`) — a designed, tested nine-rung priority ladder: Welcome,
 * "<Name>'s back.", Back on site, Early start, Good evening, Weekend shift,
 * Fresh week, Happy Friday, then time of day. The console imported it rather
 * than copying it, on the reasoning that two surfaces greeting the same
 * superintendent differently at the same moment is drift.
 *
 * That reasoning was about not RE-SPELLING the ladder, and it still holds —
 * this file spells no ladder. What changed is the console's answer to whether
 * it wants one (user-directed, 2026-09-07): it does not. "Weekend shift" and
 * "Back on site" comment on the reader's working pattern, and a desk console
 * someone opens to see what is past due has no business remarking that they
 * are in on a Saturday. The greeting is a courtesy at the top of a page; the
 * page's job is underneath it.
 *
 * ## The phone keeps the ladder
 *
 * Deliberately. The nine rungs were designed for the phone's home header,
 * where the app is greeting someone who has just walked a site — a surface
 * where "Back on site" is a welcome rather than an observation. Nothing here
 * touches `backend/src/frontend/lib/greeting.ts`, and the two surfaces are
 * now allowed to differ because the difference is a decision rather than an
 * accident. `greetingName` is still the phone's, because how a name is
 * shortened is not a per-surface question.
 *
 * ## The clock is the brief's instant, not a fresh read
 *
 * `generatedAt` is the instant every number under the greeting was computed
 * against. Reading `new Date()` here instead would let the headline and the
 * panels disagree about what day it is across a midnight or an 18:00
 * boundary. Local hours, so the console greets for where the READER is.
 */

import { greetingName } from "../../../backend/src/frontend/lib/greeting";

export { greetingName };

export interface ConsoleGreetingSignals {
  /** The instant the brief was generated against — see the file header. */
  generatedAt: Date;
  /** First name, or `""` when the caller's mirror row carries none. The
   *  greeting then drops the name rather than print a placeholder. */
  name: string;
}

/**
 * The boundaries, written once.
 *
 * Noon and 18:00, which is where every product that says these three words
 * puts them. Midnight to 05:59 is MORNING rather than a fourth branch: a
 * superintendent up at 4am does not need the console to notice.
 */
const AFTERNOON_FROM = 12;
const EVENING_FROM = 18;

export function timeOfDayGreeting(at: Date): string {
  const hour = at.getHours();
  if (hour >= EVENING_FROM) return "Good evening";
  if (hour >= AFTERNOON_FROM) return "Good afternoon";
  return "Good morning";
}

/** The greeting for this moment, as one line. */
export function consoleGreeting(signals: ConsoleGreetingSignals): string {
  const greeting = timeOfDayGreeting(signals.generatedAt);
  return signals.name ? `${greeting}, ${signals.name}.` : `${greeting}.`;
}

/**
 * The greeting on a Home scoped to one project (LED-1151): "Welcome back to
 * Glenmore Manor, Jake." — the project replaces the time of day, because the
 * reader just chose where they are. Nameless, it drops the name as
 * `consoleGreeting` does rather than print a placeholder.
 */
export function projectGreeting(project: string, name: string): string {
  return name ? `Welcome back to ${project}, ${name}.` : `Welcome back to ${project}.`;
}
